import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { NoAccess } from '@/components/custom/no-access';
import { Section } from '@/components/custom/section';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { companyApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

export const Route = createFileRoute('/_authenticated/setup/company')({
  component: CompanyPage,
});

/** Read-only label/value row for the company profile. */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}

function CompanyPage() {
  const canView = useHasPermission('company.view');

  const companyQuery = useQuery({
    queryKey: queryKeys.company.info(),
    queryFn: () => companyApi.get(),
    enabled: canView,
  });
  const company = companyQuery.data?.company ?? null;

  const exportMutation = useMutation({
    mutationFn: () => companyApi.exportLoginHistoryCsv(),
    onSuccess: ({ blob, filename }) => {
      // The server sends a dated Content-Disposition filename; the literal is
      // just a safety net for the (shouldn't-happen) missing-header case.
      triggerDownload(blob, filename ?? 'login-history.csv');
      toast.success('Login history exported');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    },
  });

  if (!canView) {
    return <NoAccess permission="company.view" resource="Company" />;
  }

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <PageHeader title="Company" subtitle="Organization-wide settings and audit tools." />

      <Section title="Company information" description="Read-only details about your organization.">
        {companyQuery.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-full max-w-xs" />
            <Skeleton className="h-9 w-full max-w-xs" />
          </div>
        ) : companyQuery.isError ? (
          <p className="text-sm text-destructive">
            {companyQuery.error instanceof ApiError
              ? companyQuery.error.message
              : 'Failed to load company information.'}
          </p>
        ) : company ? (
          <dl className="grid gap-4 sm:grid-cols-2">
            <Field label="Company name" value={company.name} />
            <Field
              label="Email domains"
              value={company.domains.length > 0 ? company.domains.join(', ') : '—'}
            />
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">No company information available.</p>
        )}
      </Section>

      <Section
        title="Login history"
        description="Download a CSV of all user logins in your organization for the last 30 days."
      >
        <div>
          <Button onClick={() => exportMutation.mutate()} disabled={exportMutation.isPending}>
            <Download />
            {exportMutation.isPending ? 'Exporting…' : 'Export login history'}
          </Button>
        </div>
      </Section>
    </PageShell>
  );
}
